import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import Toast from "../../components/Common/Toast";
import Pagination from "../../components/Common/Pagination";
import CampaignProgressModal from "../../components/Common/CampaignProgressModal";
import commonService, { normalizeListResponse } from "../../services/otherServices";
import { getErrorMessage } from "../../services/apiClient";
import { formatIST } from "../../utils/dateHelpers";
import { Search, X, ScrollText, Activity, FileText, Download, Code2, Eye } from "lucide-react";

const DEFAULT_PAGE_SIZE = 10;

const formatStatus = (status: string) => {
  if (!status) return "-";
  return status
    .toLowerCase()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const statusColors = (status: string) => {
  switch (status) {
    case "ready":
      return { bg: "#dcfce7", color: "#166534" };
    case "open":
      return { bg: "#fee2e2", color: "#991b1b" };
    case "inprogress":
      return { bg: "#dbeafe", color: "#1d4ed8" };
    default:
      return { bg: "#fef3c7", color: "#92400e" };
  }
};

// Confirmed real row shape from /logs/* — {success, count, logs}, no
// cross-page total. Same reader used on AdminLogsPage.
const readLogField = (log: any, ...keys: string[]) => {
  for (const key of keys) {
    if (log[key] !== undefined && log[key] !== null && log[key] !== "") return log[key];
  }
  return null;
};

const AdminCampaignsPage: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  // page / pageSize / search live in the URL (not useState) so that
  // navigating to a campaign's Details page and back restores exactly
  // where the user was, instead of resetting to page 1 - the list used
  // to lose this on every back-navigation because component state is
  // wiped when the route unmounts, but the URL survives browser back/forward.
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get("page")) || 1;
  const pageSize = Number(searchParams.get("limit")) || DEFAULT_PAGE_SIZE;
  const search = searchParams.get("search") || "";

  const setPage = (newPage: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("page", String(newPage));
      return next;
    });
  };

  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);

  const [searchInput, setSearchInput] = useState(search);

  const [toast, setToast] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const [logsPanel, setLogsPanel] = useState<{ campaign: any; logs: any[] } | null>(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [expandedLogRow, setExpandedLogRow] = useState<number | null>(null);

  const [progressPanel, setProgressPanel] = useState<any | null>(null);
  const [downloadingImportId, setDownloadingImportId] = useState<number | null>(null);

  const [htmlPanel, setHtmlPanel] = useState<{ campaign: any; files: any[] } | null>(null);
  const [htmlLoading, setHtmlLoading] = useState(false);
  const [downloadingHtmlName, setDownloadingHtmlName] = useState<string | null>(null);
  const [viewingHtmlName, setViewingHtmlName] = useState<string | null>(null);

  const handleDownloadImport = async (campaign: any) => {
    setDownloadingImportId(campaign.id);

    try {
      await commonService.downloadCampaignImportFile(campaign.id);
    } catch (error) {
      setToast({ type: "error", message: getErrorMessage(error, "Failed to download import file") });
    } finally {
      setDownloadingImportId(null);
    }
  };

  const openHtmlFiles = async (campaign: any) => {
    setHtmlLoading(true);

    try {
      const res = await commonService.listCampaignRawHtml(campaign.id);
      setHtmlPanel({ campaign, files: res.data?.files || [] });
    } catch (error) {
      setToast({ type: "error", message: getErrorMessage(error, "Failed to load saved HTML files") });
    } finally {
      setHtmlLoading(false);
    }
  };

  const handleDownloadHtml = async (campaignId: number, filename: string) => {
    setDownloadingHtmlName(filename);

    try {
      await commonService.downloadCampaignRawHtml(campaignId, filename);
    } catch (error) {
      setToast({ type: "error", message: getErrorMessage(error, "Failed to download HTML file") });
    } finally {
      setDownloadingHtmlName(null);
    }
  };

  const handleViewHtml = async (campaignId: number, filename: string) => {
    setViewingHtmlName(filename);

    try {
      await commonService.viewCampaignRawHtml(campaignId, filename);
    } catch (error) {
      setToast({ type: "error", message: getErrorMessage(error, "Failed to open HTML file") });
    } finally {
      setViewingHtmlName(null);
    }
  };

  const fetchCampaigns = async () => {
    setLoading(true);

    try {
      const res = await commonService.campaignList({ page, limit: pageSize, search });
      setCampaigns(res.data?.data || []);
      setTotalPages(res.data?.total_pages || 0);
      setTotalRecords(res.data?.total || 0);
    } catch (error) {
      setToast({ type: "error", message: getErrorMessage(error, "Failed to load campaigns") });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, search]);

  const handleSearch = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (searchInput) next.set("search", searchInput);
      else next.delete("search");
      next.set("page", "1");
      return next;
    });
  };

  const handlePageSizeChange = (newSize: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("limit", String(newSize));
      next.set("page", "1");
      return next;
    });
  };

  const openLogs = async (campaign: any) => {
    setLogsLoading(true);
    setExpandedLogRow(null);

    try {
      const res = await commonService.logsForCampaign(campaign.id);
      const { items } = normalizeListResponse(res);
      setLogsPanel({ campaign, logs: items });
    } catch (error) {
      setToast({ type: "error", message: getErrorMessage(error, "Failed to load campaign logs") });
    } finally {
      setLogsLoading(false);
    }
  };

  return (
    <AdminLayout>
      <style>{`
        .admin-campaigns-scrollx {
          overflow-x: scroll;
          scrollbar-width: thin;
        }
        .admin-campaigns-scrollx::-webkit-scrollbar {
          height: 10px;
        }
        .admin-campaigns-scrollx::-webkit-scrollbar-track {
          background: #f3f4f6;
        }
        .admin-campaigns-scrollx::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 6px;
        }
        .admin-campaigns-scrollx::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
      <div style={{ padding: "28px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "16px",
            marginBottom: "22px",
          }}
        >
          <div>
            <div style={{ fontSize: "26px", fontWeight: 800, color: "#111827" }}>Campaigns</div>
            <div style={{ fontSize: "13px", color: "#6b7280", marginTop: "4px" }}>
              {totalRecords} campaign{totalRecords === 1 ? "" : "s"} across every user
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: "10px",
              padding: "9px 14px",
            }}
          >
            <Search size={15} color="#9ca3af" />
            <input
              placeholder="Search file name, sender..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              style={{ border: "none", outline: "none", fontSize: "13px", width: "220px" }}
            />
            <button onClick={handleSearch} style={secondaryBtn}>
              Search
            </button>
          </div>
        </div>

        <div
          style={{
            background: "white",
            borderRadius: "18px",
            border: "1px solid #e5e7eb",
            overflow: "hidden",
            boxShadow: "0 10px 30px rgba(0,0,0,0.04)",
          }}
        >
          <div className="admin-campaigns-scrollx">
            <table style={{ width: "100%", minWidth: "1300px", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  <th style={{ ...thStyle, width: "100px", textAlign: "center" }}>Campaign ID</th>
                  <th style={thStyle}>File Name</th>
                  <th style={thStyle}>Created By</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Status</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Leads</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Emails</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Created</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td style={tdStyle} colSpan={8}>
                      Loading campaigns...
                    </td>
                  </tr>
                ) : campaigns.length === 0 ? (
                  <tr>
                    <td style={tdStyle} colSpan={8}>
                      No campaigns found
                    </td>
                  </tr>
                ) : (
                  campaigns.map((item) => {
                    const colors = statusColors(item.status);
                    return (
                      <tr key={item.id}>
                        <td style={{ ...tdStyle, width: "100px", textAlign: "center" }}>{item.id}</td>
                        <td style={tdStyle}>
                          <div style={{ fontWeight: 600, color: "#111827", maxWidth: "220px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {item.file_name}
                          </div>
                        </td>
                        <td style={tdStyle}>{item.created_by || "-"}</td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          <span
                            style={{
                              padding: "4px 10px",
                              borderRadius: "999px",
                              fontSize: "11px",
                              fontWeight: 700,
                              textTransform: "uppercase",
                              background: colors.bg,
                              color: colors.color,
                            }}
                          >
                            {formatStatus(item.status)}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>{item.data_count ?? "-"}</td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>{item.email_summary?.total_emails ?? 0}</td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          {item.created_at ? formatIST(item.created_at, { day: "2-digit", month: "2-digit", year: "numeric" }) : "-"}
                        </td>
                        <td style={tdStyle}>
                          <div style={{ display: "flex", gap: "6px" }}>
                            <button onClick={() => navigate(`/campaign-details/${item.id}`)} style={iconTextBtn}>
                              <FileText size={13} />
                              Details
                            </button>
                            <button onClick={() => setProgressPanel(item)} style={iconTextBtn}>
                              <Activity size={13} />
                              Progress
                            </button>
                            <button onClick={() => openLogs(item)} style={iconTextBtn}>
                              <ScrollText size={13} />
                              Logs
                            </button>
                            <button onClick={() => openHtmlFiles(item)} style={iconTextBtn}>
                              <Code2 size={13} />
                              HTML
                            </button>
                            {(item.status === "inprogress" || item.status === "closed") && (
                              <button
                                onClick={() => handleDownloadImport(item)}
                                disabled={downloadingImportId === item.id}
                                title="Download the CSV imported to Salesforce"
                                style={{ ...iconTextBtn, opacity: downloadingImportId === item.id ? 0.5 : 1 }}
                              >
                                <Download size={13} />
                                {downloadingImportId === item.id ? "..." : "Import File"}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {totalRecords > 0 && (
            <div style={{ padding: "16px 18px" }}>
              <Pagination
                page={page}
                total_pages={totalPages}
                total_records={totalRecords}
                page_size={pageSize}
                onPageChange={setPage}
                page_size_options={[10, 25, 50, 100]}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          )}
        </div>
      </div>

      {/* LOGS PANEL */}
      {(logsPanel || logsLoading) && (
        <div style={modalOverlay}>
          <div style={{ width: "100%", maxWidth: "780px", background: "#fff", borderRadius: "18px", overflow: "hidden", maxHeight: "82vh", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "18px 22px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 700 }}>
                  {logsPanel ? logsPanel.campaign.file_name : "Loading..."}
                </h3>
                <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                  Full event timeline for this campaign
                </div>
              </div>
              <button onClick={() => setLogsPanel(null)} style={closeBtn}>
                <X size={18} />
              </button>
            </div>

            <div className="custom-scrollbar" style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
              {logsLoading ? (
                <div style={{ padding: "40px", textAlign: "center", color: "#9ca3af" }}>Loading logs...</div>
              ) : !logsPanel || logsPanel.logs.length === 0 ? (
                <div style={{ padding: "40px", textAlign: "center", color: "#9ca3af" }}>No events recorded yet</div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#f9fafb" }}>
                      <th style={thStyle}>Time</th>
                      <th style={thStyle}>Category</th>
                      <th style={thStyle}>Event</th>
                      <th style={thStyle}>Status</th>
                      <th style={thStyle}>Message</th>
                      <th style={thStyle}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {logsPanel.logs.map((log, i) => {
                      const time = readLogField(log, "created_at", "timestamp", "logged_at");
                      const status = readLogField(log, "status");
                      return (
                        <React.Fragment key={i}>
                          <tr>
                            <td style={tdStyle}>{time ? `${formatIST(time)} IST` : "-"}</td>
                            <td style={tdStyle}>{readLogField(log, "category") || "-"}</td>
                            <td style={tdStyle}>{readLogField(log, "event_type") || "-"}</td>
                            <td style={tdStyle}>
                              {status ? (
                                <span
                                  style={{
                                    padding: "4px 10px",
                                    borderRadius: "999px",
                                    fontSize: "11px",
                                    fontWeight: 700,
                                    background: /error|fail/i.test(status) ? "#fee2e2" : "#dcfce7",
                                    color: /error|fail/i.test(status) ? "#b91c1c" : "#15803d",
                                  }}
                                >
                                  {status}
                                </span>
                              ) : (
                                "-"
                              )}
                            </td>
                            <td style={{ ...tdStyle, maxWidth: "260px" }}>
                              {readLogField(log, "message", "description", "detail") || "-"}
                            </td>
                            <td style={tdStyle}>
                              <button onClick={() => setExpandedLogRow(expandedLogRow === i ? null : i)} style={linkBtn}>
                                {expandedLogRow === i ? "Hide" : "Raw"}
                              </button>
                            </td>
                          </tr>
                          {expandedLogRow === i && (
                            <tr>
                              <td colSpan={6} style={{ ...tdStyle, background: "#f9fafb" }}>
                                <pre style={{ margin: 0, fontSize: "12px", whiteSpace: "pre-wrap" }}>
                                  {JSON.stringify(log, null, 2)}
                                </pre>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* SAVED HTML PANEL */}
      {(htmlPanel || htmlLoading) && (
        <div style={modalOverlay}>
          <div style={{ width: "100%", maxWidth: "620px", background: "#fff", borderRadius: "18px", overflow: "hidden", maxHeight: "82vh", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "18px 22px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 700 }}>
                  {htmlPanel ? `${htmlPanel.campaign.file_name} — Campaign #${htmlPanel.campaign.id}` : "Loading..."}
                </h3>
                <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                  Saved Google result-page HTML, per scraped domain
                </div>
              </div>
              <button onClick={() => setHtmlPanel(null)} style={closeBtn}>
                <X size={18} />
              </button>
            </div>

            <div className="custom-scrollbar" style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
              {htmlLoading ? (
                <div style={{ padding: "40px", textAlign: "center", color: "#9ca3af" }}>Loading saved HTML files...</div>
              ) : !htmlPanel || htmlPanel.files.length === 0 ? (
                <div style={{ padding: "40px", textAlign: "center", color: "#9ca3af" }}>
                  No saved HTML files found for this campaign
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#f9fafb" }}>
                      <th style={thStyle}>Domain</th>
                      <th style={{ ...thStyle, textAlign: "center" }}>Pages</th>
                      <th style={thStyle}>Saved</th>
                      <th style={thStyle}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {htmlPanel.files.map((file) => (
                      <tr key={file.filename}>
                        <td style={tdStyle}>{file.domain}</td>
                        <td style={{ ...tdStyle, textAlign: "center" }}>
                          {file.page_count ?? "-"}
                        </td>
                        <td style={tdStyle}>
                          {file.modified_at ? formatIST(file.modified_at) : "-"}
                        </td>
                        <td style={tdStyle}>
                          <div style={{ display: "flex", gap: "6px" }}>
                            <button
                              onClick={() => handleViewHtml(htmlPanel.campaign.id, file.filename)}
                              disabled={viewingHtmlName === file.filename}
                              style={{ ...iconTextBtn, opacity: viewingHtmlName === file.filename ? 0.5 : 1 }}
                            >
                              <Eye size={13} />
                              {viewingHtmlName === file.filename ? "..." : "View"}
                            </button>
                            <button
                              onClick={() => handleDownloadHtml(htmlPanel.campaign.id, file.filename)}
                              disabled={downloadingHtmlName === file.filename}
                              style={{ ...downloadBtn, opacity: downloadingHtmlName === file.filename ? 0.5 : 1 }}
                            >
                              <Download size={13} />
                              {downloadingHtmlName === file.filename ? "..." : "Download"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {progressPanel && (
        <CampaignProgressModal campaign={progressPanel} onClose={() => setProgressPanel(null)} />
      )}

      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
    </AdminLayout>
  );
};

const thStyle: React.CSSProperties = {
  textAlign: "left",
  padding: "12px 14px",
  fontSize: "11px",
  color: "#6b7280",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  whiteSpace: "nowrap",
  borderBottom: "1px solid #e5e7eb",
};

const tdStyle: React.CSSProperties = {
  padding: "13px 14px",
  fontSize: "13px",
  color: "#374151",
  borderBottom: "1px solid #f3f4f6",
};

const secondaryBtn: React.CSSProperties = {
  background: "#f3f4f6",
  color: "#111827",
  border: "none",
  borderRadius: "8px",
  padding: "6px 12px",
  fontWeight: 600,
  fontSize: "12px",
  cursor: "pointer",
};

const iconTextBtn: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  border: "1px solid #e5e7eb",
  background: "#fff",
  color: "#374151",
  borderRadius: "8px",
  padding: "6px 10px",
  fontSize: "12px",
  fontWeight: 600,
  cursor: "pointer",
};

// Same size/padding/border-radius/alignment/spacing as iconTextBtn - only
// the fill and text/icon color change, for the HTML modal's Download
// button specifically (matches the blue - #2563eb - already used
// elsewhere in this file for the "inprogress" status badge and linkBtn).
const downloadBtn: React.CSSProperties = {
  ...iconTextBtn,
  border: "none",
  background: "#2563eb",
  color: "#fff",
};

const linkBtn: React.CSSProperties = {
  border: "none",
  background: "transparent",
  color: "#2563eb",
  fontSize: "12px",
  fontWeight: 600,
  cursor: "pointer",
};

const modalOverlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(15,23,42,0.55)",
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px",
  backdropFilter: "blur(4px)",
};

const closeBtn: React.CSSProperties = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: "#6b7280",
};

export default AdminCampaignsPage;
